import type { Track } from '@qlk/shared';
import { AlertTriangle, Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EmptyState, OTimKiem, StatusBadge } from '../../../ui/index.js';
import { useGoiYBai } from '../api.js';
import { doDai, nhanGiayPhep } from './dinhDang.js';

export interface TrackPickerProps {
  /** Bắt buộc: dùng để kiểm tra whitelist và giấy phép cho đúng kênh. */
  channelId: string;
  value: string[];
  onChange: (ids: string[]) => void;
  /** Số bài tối đa chọn được. */
  toiDa?: number;
}

/**
 * Chọn nhạc cho một video — module 05 dùng lại (xem docs/modules/music.md).
 *
 * Chỉ hiện các bài **qua được** `checkTrackForChannel` cho kênh đang chọn, nên
 * danh sách đã loại sẵn bài hết hạn, bài chưa whitelist và nhạc kho Network
 * không thuộc Network của kênh. Bài còn cảnh báo (giấy phép sắp hết hạn) vẫn
 * chọn được nhưng hiện nhãn vàng.
 */
export function TrackPicker({ channelId, value, onChange, toiDa = 5 }: TrackPickerProps) {
  const [q, setQ] = useState('');
  const goiY = useGoiYBai(channelId, '');

  const danhSach = useMemo(() => {
    const tim = q.trim().toLowerCase();
    const bai = goiY.data ?? [];
    if (!tim) return bai;
    return bai.filter(
      (b) => b.title.toLowerCase().includes(tim) || b.artist.toLowerCase().includes(tim),
    );
  }, [goiY.data, q]);

  const daChon = new Set(value);
  const dayDu = value.length >= toiDa;

  function doi(bai: Track) {
    if (daChon.has(bai.id)) {
      onChange(value.filter((x) => x !== bai.id));
      return;
    }
    if (dayDu) return;
    onChange([...value, bai.id]);
  }

  if (!channelId) {
    return <p className="text-co-ban text-chu-phu">Chọn kênh trước để xem nhạc dùng được.</p>;
  }

  return (
    <div className="rounded-the border border-vien bg-be-mat">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-vien p-4">
        <OTimKiem value={q} onChange={setQ} label="Tìm bài nhạc" placeholder="Tên bài, nghệ sĩ…" />
        <span className="so-lieu text-phu text-chu-mo">
          Đã chọn {value.length}/{toiDa}
        </span>
      </div>

      {goiY.isPending && (
        <p className="px-4 py-5 text-co-ban text-chu-phu">Đang tìm nhạc hợp lệ…</p>
      )}

      {goiY.error && (
        <p role="alert" className="px-4 py-5 text-co-ban text-bad-chu">
          {goiY.error.message}
        </p>
      )}

      {!goiY.isPending && !goiY.error && danhSach.length === 0 && (
        <EmptyState
          icon={AlertTriangle}
          title="Không có bài nào dùng được cho kênh này"
          description="Kiểm tra whitelist và hạn giấy phép ở màn hình Kho nhạc."
        />
      )}

      <ul className="max-h-80 divide-y divide-vien overflow-y-auto">
        {danhSach.map((b) => {
          const chon = daChon.has(b.id);
          const giayPhep = nhanGiayPhep(b);
          const canhBao = giayPhep.mau !== 'text-ok-chu';

          return (
            <li key={b.id}>
              <button
                type="button"
                aria-pressed={chon}
                disabled={!chon && dayDu}
                onClick={() => {
                  doi(b);
                }}
                className="vung-bam flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-header-bang disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span
                  aria-hidden="true"
                  className={`inline-flex size-5 shrink-0 items-center justify-center rounded border ${
                    chon ? 'border-nhan bg-nhan text-white' : 'border-vien'
                  }`}
                >
                  {chon && <Check size={14} />}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-chu">{b.title}</span>
                  <span className="block truncate text-phu text-chu-mo">
                    {b.artist} · {b.sourceName} · {doDai(b.durationSec)}
                  </span>
                </span>

                {canhBao && <StatusBadge tone="warn">{giayPhep.text}</StatusBadge>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
